import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AppDateField from "../../lib/components/AppDateField";
import AppSelectField from "../../lib/components/AppSelectField";
import AppTextField from "../../lib/components/AppTextField";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import organizationservice from "../../service/organizationservice";
import moment from "moment";
import PreloaderTable from "../../lib/components/PreloaderTable";
import SubscribersTable from "./components/subscriber-data-table";
import scrollHandler from "../../utils/scrollHandler";
import AppTab from "../../components/tab-label";
import useDebounce from "../../hooks/useDebounce";
import AppExportButton from "../../lib/components/AppExportButton";
import AppExportModal, { EXPORT_HEADER_OPTIONS } from "../../lib/components/AppExportModal";

const tabList = [
  { label: "All", value: "ALL" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Bi-annually", value: "BIANNUALLY" },
  { label: "Annually", value: "ANNUALLY" },
];

const Organizations = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const plan = searchParams.get("plan") || "";
  const keyword = searchParams.get("keyword") || "";
  const dateFrom = searchParams.get("dateFrom") || null;
  const dateTo = searchParams.get("dateTo") || null;
  const subscriptionStatus = searchParams.get("subscriptionStatus") || "";
  const state = searchParams.get("state") || "";
  const isFreeTrial = searchParams.get("isFreeTrial") || "";
  const searchTerm = useDebounce(keyword);
  const [isExportOpen, setIsExportOpen] = React.useState(false);

  const queries = {
    page,
    limit,
    plan,
    keyword: searchTerm,
    dateFrom,
    dateTo,
    subscriptionStatus,
    state,
    isFreeTrial,
  };

  const organizationsQuery = useQuery({
    retry: false,
    queryKey: ["organizations", ...Object.values(queries)],
    queryFn: () => organizationservice.get({ ...queries }),
  });

  const statsQuery = useQuery({
    retry: false,
    enabled: !state,
    queryKey: ["subs-period-stats", ...Object.values(queries)],
    queryFn: () => organizationservice.subscriptionPeriodStats({ ...queries }),
  });

  const { isPending: isLoading, data } = organizationsQuery;
  const { data: stats } = statsQuery;

  const organizationData = data?.data;
  const statsData = stats?.data?.data;

  return (
    <AppLayout>
      <Header title="Subscribers" description="Get insights to accounts on Jureb here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className="d-flex mb-4 justify-content-end">
          <AppExportButton onClick={() => setIsExportOpen(true)} />
        </div>
        <div className="row mb-5">
          <div className="col-6 col-md-3 col-lg-4 mb-3">
            <div>
              <AppTextField
                placeholder="Search organizations..."
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField label="Date From" value={dateFrom} onChange={(e) => handleChange(e, "dateFrom")} />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField label="Date To" value={dateTo} minDate={dateFrom} onChange={(e) => handleChange(e, "dateTo")} />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppSelectField
                value={subscriptionStatus}
                placeholder="Status"
                onChange={(e) => handleChange(e, "subscriptionStatus")}
                items={[
                  { label: "All", value: "ALL" },
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                  { label: "Expired", value: "EXPIRED" },
                ]}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppSelectField
                value={isFreeTrial}
                placeholder="Subscription"
                onChange={(e) => handleChange(e, "isFreeTrial")}
                items={[
                  { label: "All", value: "ALL" },
                  { label: "Paid Subscribers", value: "false" },
                  { label: "Free Trial Subscribers", value: "true" },
                ]}
              />
            </div>
          </div>
        </div>
        <AppTab items={tabList} onChange={handleChangeTab} value={plan || "ALL"} statsMap={statsData} />
        <PreloaderTable isLoading={isLoading} />
        <SubscribersTable
          isLoading={isLoading}
          data={organizationData?.data}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={organizationData?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) => handleChange({ target: { value } }, "limit")}
        />
      </div>
      <AppExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        headerOptions={EXPORT_HEADER_OPTIONS.organizations}
        showDateRange
        defaultFilters={{
          keyword: searchTerm,
          plan,
          subscriptionStatus,
          state,
          isFreeTrial,
          dateFrom,
          dateTo,
        }}
        onExport={(params) => organizationservice.export(params)}
      />
    </AppLayout>
  );

  function handleChangeTab(_, value) {
    searchParams.set("plan", value);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }

  function handleChange(e, name = "") {
    const value = name.includes("date") ? moment(e).format("YYYY-MM-DD") : name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
    if (name === "page") scrollHandler();
  }
};

export default Organizations;
